import { Alert } from "../Alert";
import { FileCard } from "./FileCard";
import type { SearchResponse } from "../../api/types";

/**
 * Every file one search returned, on its own page.
 *
 * Rendered from the payload the turn already holds — it never re-runs the search. That is the
 * point: re-querying could return a different set from the one the answer was written against, and
 * a page headed "all results" showing a *later* corpus, or a set filtered against a since-refreshed
 * credential, would be quietly lying about which files the answer came from.
 *
 * The scope line, the index notice and the withheld count are repeated here rather than left
 * behind on the conversation, because this page is read on its own and each of them qualifies what
 * the list means.
 */
export function AllResults({
  result,
  onBack,
}: {
  result: SearchResponse;
  onBack: () => void;
}): JSX.Element {
  const { index_status: status, permissions } = result;
  return (
    <div className="all-results">
      <div className="all-results__head">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back to the conversation
        </button>
        <h2>
          {result.hits.length} {result.hits.length === 1 ? "result" : "results"} in{" "}
          <span className="search-results__domain">
            {result.domain_restriction.join(", ") || "all files"}
          </span>
        </h2>
        <p className="search-results__empty">
          Searched {result.files_searched_count.toLocaleString()}{" "}
          {result.files_searched_count === 1 ? "file" : "files"}.
        </p>
      </div>

      {status.notice && (
        <Alert variant={status.nothing_to_search ? "info" : "warning"}>{status.notice}</Alert>
      )}

      {result.hits.length === 0 && !status.nothing_to_search && (
        <p className="search-results__empty">No files in this scope matched.</p>
      )}

      {result.results_truncated && (
        <Alert variant="warning">
          The search stopped at {result.result_limit_applied} results, so more files may match than
          are listed here. Narrow the question to see different ones.
        </Alert>
      )}

      <div className="all-results__list">
        {result.hits.map((hit) => (
          <FileCard key={hit.file_id} hit={hit} />
        ))}
      </div>

      {permissions.hidden_count > 0 && (
        <p className="search-results__withheld">
          {permissions.hidden_count} further{" "}
          {permissions.hidden_count === 1 ? "result was" : "results were"} withheld because you may
          not open {permissions.hidden_count === 1 ? "it" : "them"}.
          {permissions.notice && ` ${permissions.notice}`}
        </p>
      )}
    </div>
  );
}
